/**
 * EltJobExecutionsPage Component
 *
 * Main page for viewing and managing job executions.
 * Uses server-side sorting and offset-based pagination.
 */

import React, { useState, useEffect } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { DashboardLayout } from "../../../components/layout/DashboardLayout";
import { Button } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";
import { Alert } from "../../../components/ui/Alert";
import { Pagination } from "../../../components/ui/Pagination";
import { EltJobExecutionTable } from "../components/EltJobExecutionTable";
import { EltJobExecutionDetailsModal } from "../components/EltJobExecutionDetailsModal";
import { CancelEltJobExecutionModal } from "../components/CancelEltJobExecutionModal";
import { useEltJobExecutions } from "../hooks/useEltJobExecutions";
import { useAuth } from "../../../contexts/AuthContext";
import { useLayoutProps } from "../../../hooks/useLayoutProps";
import type { EltJobExecution, EltJobExecutionStatus } from "../../../types/elt-job-execution.types";
import { getErrorMessage } from "../../../lib/utils/errors";

const PAGE_SIZE = 20;

export const EltJobExecutionsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState<EltJobExecution | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [executionToCancel, setExecutionToCancel] = useState<EltJobExecution | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || "";

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [statusFilter]);

  // Fetch job executions with server-side sorting and pagination
  const {
    executions,
    pagination,
    stats,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEltJobExecutions(tenantId, {
    status: (statusFilter as EltJobExecutionStatus) || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const handleViewDetails = (execution: EltJobExecution) => {
    setSelectedExecution(execution);
    setShowDetailsModal(true);
  };

  const handleCancelExecution = (execution: EltJobExecution) => {
    setExecutionToCancel(execution);
    setShowCancelModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Job Executions</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>View ELT job execution history and results</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="card rounded-lg shadow p-4">
            <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stats.total}</div>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Executions</div>
          </div>
          <div className="card rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-accent-600">{stats.succeeded}</div>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Succeeded</div>
          </div>
          <div className="card rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Failed</div>
          </div>
          <div className="card rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>In Progress</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="w-48">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
            </Select>
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Showing {executions.length} of {pagination.total} executions
          </div>
        </div>

        {/* Error Alert */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Job Executions Table */}
        <EltJobExecutionTable executions={executions} isLoading={isLoading} onView={handleViewDetails} onCancel={handleCancelExecution} />

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Pagination
            page={page}
            total={pagination.total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedExecution && (
          <EltJobExecutionDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedExecution(null);
            }}
            tenantId={tenantId}
            jobExecutionId={selectedExecution.jobExecutionId}
          />
        )}

        {/* Cancel Modal */}
        {showCancelModal && executionToCancel && (
          <CancelEltJobExecutionModal
            isOpen={showCancelModal}
            onClose={() => {
              setShowCancelModal(false);
              setExecutionToCancel(null);
            }}
            tenantId={tenantId}
            execution={executionToCancel}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
